import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface NotificationSettings {
  pushNotifications: boolean;
  emailNotifications: boolean;
  smsNotifications: boolean;
  newMatches: boolean;
  newMessages: boolean;
  superLikes: boolean;
  profileViews: boolean;
  promotions: boolean;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  quietHours: boolean;
  quietStart: string;
  quietEnd: string;
}

interface NotificationContextType {
  settings: NotificationSettings;
  updateSettings: (newSettings: Partial<NotificationSettings>) => void;
  sendNotification: (title: string, body: string, type?: string) => void;
  isQuietTime: () => boolean;
}

const defaultSettings: NotificationSettings = {
  pushNotifications: true,
  emailNotifications: true,
  smsNotifications: false,
  newMatches: true,
  newMessages: true,
  superLikes: true,
  profileViews: false,
  promotions: true,
  soundEnabled: true,
  vibrationEnabled: true,
  quietHours: false,
  quietStart: '22:00',
  quietEnd: '08:00'
};

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<NotificationSettings>(defaultSettings);

  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('piDatingNotificationSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings({ ...defaultSettings, ...parsed });
      } catch (error) {
        console.error('Error loading notification settings:', error);
      }
    }

    // Request notification permission on load
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const updateSettings = (newSettings: Partial<NotificationSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    localStorage.setItem('piDatingNotificationSettings', JSON.stringify(updatedSettings));
  };

  const isQuietTime = (): boolean => {
    if (!settings.quietHours) return false;

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const [startHour, startMin] = settings.quietStart.split(':').map(Number);
    const [endHour, endMin] = settings.quietEnd.split(':').map(Number);
    
    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;

    if (startTime <= endTime) {
      return currentTime >= startTime && currentTime <= endTime;
    } else {
      // Quiet hours span midnight
      return currentTime >= startTime || currentTime <= endTime;
    }
  };

  const sendNotification = (title: string, body: string, type: string = 'general') => {
    // Check if notifications are enabled
    if (!settings.pushNotifications) return;

    // Check if it's quiet time
    if (isQuietTime()) return;

    // Check if this type of notification is enabled
    const typeSettings: { [key: string]: boolean } = {
      match: settings.newMatches,
      message: settings.newMessages,
      superlike: settings.superLikes,
      view: settings.profileViews,
      promotion: settings.promotions
    };

    if (type in typeSettings && !typeSettings[type]) return;

    // Send browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/logo 3 BR.png',
        tag: type,
        requireInteraction: type === 'match' || type === 'message'
      });

      // Play sound if enabled
      if (settings.soundEnabled) {
        const audio = new Audio('/notification-sound.mp3');
        audio.play().catch(() => {
          // Fallback to system sound
          console.log('Playing notification sound');
        });
      }

      // Vibrate if enabled and supported
      if (settings.vibrationEnabled && 'vibrate' in navigator) {
        navigator.vibrate([200, 100, 200]);
      }

      // Auto-close notification after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);
    }
  };

  return (
    <NotificationContext.Provider value={{
      settings,
      updateSettings,
      sendNotification,
      isQuietTime
    }}>
      {children}
    </NotificationContext.Provider>
  );
};